import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import TrackingTopBar from 'src/components/ui/TrackingTopBar/TrackingTopBar';
import { useTranslation } from 'react-i18next';
import WarningBox, { WarningBoxColorType } from 'src/components/ui/WarningBox/WarningBox';
import KeyValueCard, { KeyValueCardProps } from 'src/components/ui/KeyValueCard/KeyValueCard';
import Icon from 'src/components/ui/Icon/Icon';
import Divider from 'src/components/ui/Divider/Divider';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import NoDataPlaceholder from 'src/components/layouts/NoDataPlaceholder/NoDataPlaceholder';
import ProductCard from 'src/components/ui/ProductCard/ProductCard';
import SimpleFooter from 'src/components/layouts/SimpleFooter/SimpleFooter';
import CreateSupportTicketOverlay from 'src/components/layouts/CreateSupportTicketOverlay/CreateSupportTicketOverlay';
import DeliveryStatusModal from 'src/components/modals/DeliveryStatusModal/DeliveryStatusModal';
import { useNavigate, useParams } from 'react-router-dom';
import { formatOrderDate, formatDateTime } from 'src/utils/general';
import AiChatOverlay from 'src/components/layouts/AiChatOverlay/AiChatOverlay';
import Timeline from 'src/components/layouts/Timeline/Timeline';
import { OptionItemProps } from 'src/components/ui/OptionItem/OptionItem';
import TopBar from 'src/components/ui/TopBar/TopBar';
import styles from './styles/Tracking.module.scss';
import { useCompanyLogoQuery, useCustomerTrackingDetailsQuery } from '../services/customerTracking';
import { socialIcons } from '../entities';

function TrackingDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [supportTicketModalOpen, setSupportTicketModalOpen] = useState(false);
  const [statusInfoModalOpen, setStatusInfoModalOpen] = useState(false);
  const [aiChatModalOpen, setAiChatModalOpen] = useState(false);

  const handleOverlayClose = () => {
    setSupportTicketModalOpen(false);
    setAiChatModalOpen(false);
  };

  const handleModalClose = () => {
    setStatusInfoModalOpen(false);
  };

  const { trackingKey } = useParams();
  const keyString = String(trackingKey);
  const { data: details, status } = useCustomerTrackingDetailsQuery(keyString);

  const buttons = [
    {
      tone: ButtonToneProps.SECONDARY,
      title: t('tracking.create_support_ticket'),
      onClick: () => setSupportTicketModalOpen(!supportTicketModalOpen),
    },
    {
      tone: ButtonToneProps.PRIMARY_BLUE,
      title: t('tracking.open_chat'),
      onClick: () => setAiChatModalOpen(!aiChatModalOpen),
    },
  ];

  const { data: logo } = useCompanyLogoQuery(keyString);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!(logo instanceof Blob)) return;

    const url = URL.createObjectURL(logo);
    setLogoUrl(url);
  }, [logo]);

  const trackingDetails = [
    {
      label: t('tracking.seller'),
      value: details?.seller?.name,
      type: 'text',
    },
    {
      label: t('tracking.enter_order_number'),
      value: details?.order_number,
      type: 'number',
    },
    {
      label: t('tracking.order_date'),
      value: details?.order_date ? formatOrderDate(details?.order_date) : '-',
      type: 'text',
    },
    {
      label: t('tracking.title'),
      value: details?.key,
      type: 'text',
    },
    {
      label: t('tracking.carrier'),
      value: details?.carrier?.name,
      type: 'imageAndText',
      imgSrc: logoUrl || '',
    },
  ] as KeyValueCardProps['items'];

  const navigateToReturn = () => {
    localStorage.setItem('trackingKey', keyString);
    navigate(`/return/${details?.order_number}`);
  };

  type Step = {
    title: string;
    datetime: string;
    state: 'completed' | 'pending' | 'failed';
  };

  const steps: Step[] =
    details?.event?.map(event => ({
      title: event?.body?.message || '',
      datetime: event?.timestamp ? formatDateTime(event?.timestamp) : '',
      state: 'completed',
    })) || [];

  const menuItems: OptionItemProps[] = [
    {
      label: t('tracking.create_support_ticket'),
      onClick: () => setSupportTicketModalOpen(!supportTicketModalOpen),
    },
    {
      label: t('tracking.open_chat'),
      onClick: () => setAiChatModalOpen(!aiChatModalOpen),
      accessoryItem: 'ai-chat',
    },
  ];

  return (
    <div className={classNames(styles.root)}>
      <div className={styles.topbar}>
        {status === 'success' ? (
          <TrackingTopBar imgSrc={details?.seller?.logo_url || ''} buttons={buttons} menuItems={menuItems} />
        ) : (
          <TopBar />
        )}
      </div>
      <div className={styles.main}>
        {status === 'success' && (
          <>
            <div className={styles.content}>
              <div className="relative w-full px-20 lg:px-60 py-32 md:py-48 flex-1 flex flex-col gap-y-24">
                <div className="flex justify-between">
                  <h2 className="typo-text-16--bold text-full">{t('tracking.progress')}</h2>
                  <div className="typo-text-14 text-faded flex gap-8 items-center">
                    <span className="capitalize">{t(`status.${details?.state}`)}</span>
                    <Button
                      tone={ButtonToneProps.GHOST}
                      icon="infoCircle"
                      onClick={details?.state ? () => setStatusInfoModalOpen(true) : () => {}}
                    />
                  </div>
                </div>
                {details?.estimated_delivery_date && (
                  <WarningBox
                    title={t('tracking.estimated_delivery_date')}
                    copy={details?.estimated_delivery_date ? formatOrderDate(details?.estimated_delivery_date) : '-'}
                    colorType={WarningBoxColorType.Successful}
                    icon="package"
                  />
                )}
                <KeyValueCard
                  items={trackingDetails.map(({ label, value, type, imgSrc, alt }) => ({
                    label,
                    value,
                    type,
                    imgSrc,
                    alt,
                  }))}
                />
                <div className="flex justify-between pt-12">
                  <h2 className="typo-text-16--bold text-full">{t('tracking.delivery_address')}</h2>
                </div>
                <div className="typo-text-14 flex gap-8 items-start">
                  <Icon name="pin" />
                  <address className="not-italic">
                    <p>{details?.recipient?.address.street}</p>
                    {details?.recipient?.address.street2 && <p>{details?.recipient.address.street2}</p>}
                    <p>
                      <span>{details?.recipient?.address.zip_code}</span>,{' '}
                      <span>{details?.recipient?.address.city}</span>
                    </p>
                    <span>{details?.recipient?.address.country_name}</span>
                    <p className="text-faded">{details?.recipient?.phone}</p>
                  </address>
                </div>
                <Divider className="mx-20 my-12" />
                {steps.length > 0 && <Timeline steps={steps.reverse()} />}
                {details?.is_returnable && (
                  <Button
                    tone={ButtonToneProps.PRIMARY_BLUE}
                    title={t('tracking.create_return')}
                    className="!h-[48px] !max-h-none"
                    onClick={() => navigateToReturn()}
                  />
                )}
              </div>
            </div>
            <div className="w-full relative">
              {details?.order_lines?.length ? (
                <>
                  <Divider className="md:hidden mx-20" />
                  <div className={classNames(styles.orders)}>
                    <div className="flex justify-between">
                      <div className="flex flex-col gap-y-12">
                        <h1 className="typo-title-36">{t('tracking.my_order')}</h1>
                        <p className="typo-text-16 text-faded">{formatDateTime(details?.order_date)}</p>
                      </div>
                      {details?.is_returnable && (
                        <Button
                          tone={ButtonToneProps.SECONDARY}
                          title={t('tracking.create_return')}
                          onClick={() => navigateToReturn()}
                        />
                      )}
                    </div>

                    <div className={styles.products}>
                      {details?.order_lines?.map(({ product, quantity }) => (
                        <ProductCard
                          key={product.id}
                          card={{ id: product?.id, title: product.name, copy: `${quantity}x` }}
                        />
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex justify-center items-center w-full h-full">
                  <NoDataPlaceholder headline={t('tracking.no_products_found')} />
                </div>
              )}
              {supportTicketModalOpen && <CreateSupportTicketOverlay onClose={handleOverlayClose} />}
              {aiChatModalOpen && <AiChatOverlay onClose={handleOverlayClose} />}
              {statusInfoModalOpen && (
                <DeliveryStatusModal isOpen={statusInfoModalOpen} onClose={handleModalClose} status={details?.state} />
              )}
            </div>
          </>
        )}
        {status === 'pending' && (
          <div className="flex justify-center items-center w-full h-full">
            <NoDataPlaceholder copy={t('tracking.loading')} imgSrc="/assets/illustrations/working.png" />
          </div>
        )}
        {status === 'error' && (
          <div className="flex justify-center items-center w-full h-full">
            <NoDataPlaceholder
              headline={t('tracking.not_found')}
              copy={t('tracking.not_found_description')}
              imgSrc="/assets/illustrations/not-found.png"
            />
          </div>
        )}
      </div>
      <div className="hidden md:flex">
        <SimpleFooter socialIcons={socialIcons} />
      </div>
    </div>
  );
}

export default TrackingDetails;
